import { createSlice } from '@reduxjs/toolkit';

import { getCurrentDateInFormat, getCurrentTime } from '../../utils/dateutils';

const initialState = {
  dateOfBilling: getCurrentDateInFormat(),
  timeOfBilling: getCurrentTime(),
  clientNumber: '',
  clientName: '',
  gender: '',
  birthday: '',
  anniversary: '',
  clientAddress: '',
  clientGstNumber: '',
  clientPos: '',
  products: [],
  services: [],
  memberships: [], // For membership purchases
  packages: [],    // For package purchases
  wallets: [],
  amountAdded: 0,
  walletAmount: 0,
  subTotal: 0,
  useMembership: false,
  usePackage: false,
  discount: {
    discountType: '%',
    discount: '',
  },
  taxes: {
    taxType: 'Inclusive',
    SGST: 0,
    PGST: 0,
  },
  productTaxes: {
    productTaxType: 'Inclusive',
    productSGST: 0,
    productPGST: 0,
  },
  totalAmount: 0,
  useRewardPoint: false,
  totalTaxAmount: 0,
  usedRewardCount: 0,
  perRewardPointValue: 0,
  amountPayable: 0,
  coupon: {
    couponId: null,
    couponCode: '',
    discountAmount: 0,
  },
  amountPaid: [
    {
      paymentType: 'Cash',
      amount: '',
    },
  ],
  paidDues: 0,
  notes: '',
  notesForClient: '',
  draftBillId: '',
  packageUniqueId: [],
  sendSms: true,
  sendWhatsapp: true
};

function calculatePriceSubTotal(items) {
  return items.reduce((total, item) => {
    const price = Number(item.price) || 0;
    const quantity = Number(item.quantity) || 1;
    return total + (price * quantity);
  }, 0);
}
//discount apply on the base price (price)
// const calculateItemAmount = (item) => {
//   const price = Number(item.price) || 0;
//   const discount = Number(item.discount) || 0;
//   const quantity = Number(item.quantity) || 1;

//   if (item.discountType === 'INR') {
//     const taxableAmount = Math.max(0, price - discount);
//     return (taxableAmount * quantity).toFixed(2);
//   } else {
//     const taxableAmount = price * (1 - discount / 100);
//     return (Math.max(0, taxableAmount) * quantity).toFixed(2);
//   }
// };

//discount apply on the taxable value 
// const calculateItemAmount = (item) => {
//   const price = Number(item.price) || 0;
//   const discount = Number(item.discount) || 0;
//   const quantity = Number(item.quantity) || 1;
//   const taxStatus = item.taxStatus || 'noGst';
//   const gstRate = Number(item.gst ?? 5);

//   // Extract base taxable price depending on taxStatus
//   const baseTaxablePrice = (taxStatus === 'withTax' && gstRate > 0)
//     ? price / (1 + gstRate / 100)  // inclusive: extract GST first
//     : price;                        // exclusive or noGst: price is already base

//   let amountAfterDiscount;

//   if (item.discountType === 'INR') {
//     amountAfterDiscount = Math.max(0, baseTaxablePrice - discount);
//   } else {
//     amountAfterDiscount = baseTaxablePrice * (1 - discount / 100);
//   }

//   return (Math.max(0, amountAfterDiscount) * quantity).toFixed(2);
// };
const calculateItemAmount = (item) => {
  const price = Number(item.price) || 0;
  const discount = Number(item.discount) || 0;
  const quantity = Number(item.quantity) || 1;
  const taxStatus = item.taxStatus || 'noGst';
  const gstRate = Number(item.gst ?? 5);

  let finalAmount;

  if (taxStatus === 'withTax' && gstRate > 0) {
    // INCLUSIVE TAX FLOW:
    // STEP 1: Reverse calculate taxable value from inclusive price
    const taxableValue = price / (1 + gstRate / 100);

    // STEP 2: Apply discount on the taxable value
    let discountedTaxable;
    if (item.discountType === 'INR') {
      // INR discount is a flat amount, applied once on the total (taxableValue * quantity)
      const totalTaxable = taxableValue * quantity;
      discountedTaxable = Math.max(0, totalTaxable - discount);
      // STEP 3: Add GST back on the discounted taxable total
      return (discountedTaxable * (1 + gstRate / 100)).toFixed(2);
    } else {
      discountedTaxable = taxableValue * (1 - discount / 100);
    }

    // STEP 3: Add GST back on the discounted taxable value (for % discount, multiply by qty)
    finalAmount = Math.max(0, discountedTaxable) * (1 + gstRate / 100);
    return (Math.max(0, finalAmount) * quantity).toFixed(2);
  } else {
    // EXCLUSIVE or NO GST FLOW:
    if (item.discountType === 'INR') {
      // INR discount is a flat amount, applied once on the total (price * quantity)
      const totalPrice = price * quantity;
      finalAmount = Math.max(0, totalPrice - discount);
      return finalAmount.toFixed(2);
    } else {
      finalAmount = price * (1 - discount / 100);
    }
  }

  // Multiply by quantity (only for % discount)
  return (Math.max(0, finalAmount) * quantity).toFixed(2);
};

const calculateSubTotal = (items) => {
  return items.reduce((total, item) => total + Number(item.amount || 0), 0);
};

// const calculateTaxes = (subTotal, taxType, SGST) => {
//   let SGSTAmount = 0;
//   if (taxType === 'Exclusive') {
//     SGSTAmount = (SGST / 100) * subTotal;
//   }
//   return { SGSTAmount };
// };


// Separate tax calculation functions for different item types
const calculateTaxes = (subTotal, taxType, SGST, PGST) => {
  let SGSTAmount = 0;
  let PGSTAmount = 0;
  const totalRate = (Number(SGST) || 0) + (Number(PGST) || 0);

  if (taxType === 'Exclusive') {
    SGSTAmount = ((Number(SGST) || 0) / 100) * subTotal;
    PGSTAmount = ((Number(PGST) || 0) / 100) * subTotal;
  }
  else if (taxType === 'Inclusive' || taxType === 'Ingst') {
    const totalTaxContent = subTotal - (subTotal / (1 + totalRate / 100));
    if (totalRate > 0) {
      // Distribute the tax content based on the provided rates
      SGSTAmount = totalTaxContent * (Number(SGST) / totalRate);
      PGSTAmount = totalTaxContent * (Number(PGST) / totalRate);
    }
  }

  return { SGSTAmount, PGSTAmount };
};

// const updateBillTotals = (state) => {
//   // Calculate subtotal from all item types
//   let subTotal = calculateSubTotal([
//     ...state.services,
//     ...state.products,
//     ...state.memberships,
//     ...state.packages
//   ]);

//   // ✅ Add wallet amount if any
//   if (state.walletAmount > 0) {
//     subTotal += state.walletAmount;
//   }

//   // const { SGSTAmount } = calculateTaxes(subTotal, state.taxes.taxType, state.taxes.SGST);
//    const { SGSTAmount } = calculateTaxes(
//     subTotal, 
//     state.taxes.taxType, 
//     state.taxes.SGST, 

//   );

//   const totalAmount = subTotal + SGSTAmount 
//   // const totalAmount = subTotal + SGSTAmount;

//   // Apply coupon discount if exists
//   const afterCoupon = totalAmount - (state.coupon?.discountAmount || 0);

//   // Calculate reward discount
//   let rewardDiscount = 0;
//   if (state.useRewardPoint && state.perRewardPointValue > 0) {
//     const maxRewardValue = subTotal;
//     const attemptedRewardValue = state.usedRewardCount * state.perRewardPointValue;

//     if (attemptedRewardValue > maxRewardValue) {
//       state.usedRewardCount = 0;
//     } else {
//       rewardDiscount = attemptedRewardValue;
//     }
//   }

//   rewardDiscount = Math.min(rewardDiscount, afterCoupon);
//   const amountPayable = Math.max(0, afterCoupon - rewardDiscount);

//   state.subTotal = Math.round(subTotal);
//   state.totalAmount = Math.round(totalAmount);
//   state.amountPayable = Math.round(amountPayable);

//   const totalPaid = state.amountPaid.reduce((acc, payment) =>
//     acc + Number(payment.amount || 0), 0);
//   state.paidDues = Math.round(Math.max(0, amountPayable - totalPaid));
// }
const updateBillTotals = (state) => {
  const totalSubTotal = calculateSubTotal([
    ...state.services, ...state.products, ...state.memberships, ...state.packages
  ]) + (state.walletAmount > 0 ? state.walletAmount : 0);

  let taxToAdd = 0;
  let taxToSubtract = 0;
  let totalTaxAmount = 0;

  // Process all items for per-item tax calculation
  const categories = [
    { items: state.services, defaultGst: 5 },
    { items: state.memberships, defaultGst: 5 },
    { items: state.packages, defaultGst: 5 },
    { items: state.products, defaultGst: 18 }
  ];

  categories.forEach(({ items, defaultGst }) => {
    items.forEach(curr => {
      const gstPercent = Number(curr?.gst ?? defaultGst);
      const amount = Number(curr?.amount ?? 0);
      const taxStatus = curr?.taxStatus || 'noGst';

      if (taxStatus === 'withoutTax') {
        const tax = amount * (gstPercent / 100);
        taxToAdd += tax;
        totalTaxAmount += tax;
      } else if (taxStatus === 'withTax') {
        const tax = amount - (amount / (1 + gstPercent / 100));
        taxToSubtract += tax;
        totalTaxAmount += tax;
      }
    });
  });

  const totalAmount = totalSubTotal + taxToAdd;
  const taxableSubTotal = totalSubTotal - taxToSubtract;

  state.totalTaxAmount = Math.round(totalTaxAmount);

  // Apply coupon discount if exists
  const afterCoupon = totalAmount - (state.coupon?.discountAmount || 0);

  // Calculate reward discount
  let rewardDiscount = 0;
  if (state.useRewardPoint && state.perRewardPointValue > 0) {
    const maxRewardValue = totalSubTotal;
    const attemptedRewardValue = state.usedRewardCount * state.perRewardPointValue;

    if (attemptedRewardValue > maxRewardValue) {
      state.usedRewardCount = 0;
    } else {
      rewardDiscount = attemptedRewardValue;
    }
  }

  rewardDiscount = Math.min(rewardDiscount, afterCoupon);
  const amountPayable = Math.max(0, afterCoupon - rewardDiscount);

  state.subTotal = Math.round(taxableSubTotal);
  state.totalAmount = Math.round(totalAmount);
  state.amountPayable = Math.round(amountPayable);

  const totalPaid = state.amountPaid.reduce((acc, payment) =>
    acc + Number(payment.amount || 0), 0);
  state.paidDues = Math.round(Math.max(0, amountPayable - totalPaid));
};

function getInitialBillingState() {
  return {
    dateOfBilling: getCurrentDateInFormat(),
    timeOfBilling: getCurrentTime(),
    clientNumber: '',
    clientName: '',
    gender: '',
    birthday: '',
    anniversary: '',
    clientAddress: '',
    clientGstNumber: '',
    products: [],
  // Add gst and taxStatus to service initial state
  // services: [],
    services: [],
    memberships: [], // For membership purchases
    packages: [],    // For package purchases
    wallets: [],
    amountAdded: 0,
    walletAmount: 0,
    subTotal: 0,
    useMembership: false,
    usePackage: false,
    discount: {
      discountType: '%',
      discount: '',
    },
    taxes: {
      taxType: 'Inclusive',
      SGST: 0,
      PGST: 0,
    },
    productTaxes: {
      productTaxType: 'Inclusive',
      productSGST: 0,
      productPGST: 0,
    },
    totalAmount: 0,
    useRewardPoint: false,
    usedRewardCount: 0,
    perRewardPointValue: 0,
    amountPayable: 0,
    coupon: {
      couponId: null,
      couponCode: '',
      discountAmount: 0,
    },
    amountPaid: [
      {
        paymentType: 'Cash',
        amount: '',
      },
    ],
    paidDues: 0,
    notes: '',
    noteForClient: '',
    draftBillId: '',
    packageUniqueId: [],
    sendSms: true,
    sendWhatsapp: true
  };
}

const billSlice = createSlice({
  name: 'bill',
  initialState,
  reducers: {
    resetBillState: () => getInitialBillingState(),

    handleClientsInputChange: (state, action) => {
      const { name, value } = action.payload;
      state[name] = value;
    },

    handleClientSuggestedClick: (state, action) => {
      const { clientName, clientNumber, gender, clientGstNumber, clientAddress, clientPos } = action.payload;
      state.clientName = clientName;
      state.clientNumber = clientNumber;
      state.gender = gender;
      state.clientAddress = clientAddress;
      state.clientGstNumber = clientGstNumber;
      if (clientPos) state.clientPos = clientPos;
    },

    handleProductsInputChange: (state, action) => {
      const { name, value, index } = action.payload;
      const product = state.products[index];

      if (product) {
        // Don't allow discount changes if coupon is applied or it's a membership product
        if ((name === 'discount' || name === 'discountType') &&
          (product.discountDisabledByCoupon || product.membershipApplied)) {
          return;
        }
        let updatedValue = value;

        // For inclusive items, discount is applied on taxable value
        const productGstRate = Number(product.gst ?? 18);
        const productMaxDiscountBase = (product.taxStatus === 'withTax' && productGstRate > 0)
          ? (Number(product.price) / (1 + productGstRate / 100)) * Number(product.quantity)
          : Number(product.price * product.quantity);

        if (name === 'discount' && product.discountType === '%' && Number(value) > 99) {
          updatedValue = 0;
        }
        // if (name === 'discount' && product.discountType === 'INR' && Number(value) > productMaxDiscountBase - 1) {
        //   updatedValue = 0;
        // }
        if (
            name === 'discount' &&
            product.discountType === 'INR' &&
            productMaxDiscountBase > 0 &&
            Number(value) > productMaxDiscountBase - 1
          ) {
            updatedValue = 0;
          }

        // ✅ If changing discountType to % and previous discount is > 100 → reset discount to 0
        if (name === 'discountType' && value === '%' && Number(product.discount) > 99) {
          product.discount = 0;
        }
        // if (name === 'discountType' && value === 'INR' && Number(product.discount) > productMaxDiscountBase - 1) {
        //   product.discount = 0;
        // }
        if (
          name === 'discountType' &&
          value === 'INR' &&
          productMaxDiscountBase > 0 &&
          Number(product.discount) > productMaxDiscountBase - 1
        ) {
          product.discount = 0;
        }

        product[name] = updatedValue;

        if (name === 'discount' || name === 'discountType' || name === 'price' || name === 'quantity') {
          product.amount = calculateItemAmount(product);
        }
      }

      updateBillTotals(state);
    },

    handleProductsSuggestedClick: (state, action) => {
      const { product, index, item: clientMembership } = action.payload;
      if (index === null || index === undefined) return;

      const isMembershipProduct = clientMembership?.products?.some(
        p => p.id === product._id
      );

      const isDiscountMembership = clientMembership?.membershipType === 'discountType';

      state.products[index] = {
        product: product._id,
        productName: product.productName,
        quantity: 1,
        gst: product.GST ?? 18, // written by mohit Note: please muje maaf karo mai majboor hu ye code sach mai bah
        taxStatus: product.taxStatus || 'noGst',
        providerStaff: null,
        discount: isDiscountMembership
          ? clientMembership.discount?.discountOnProduct || 0
          : isMembershipProduct
            ? clientMembership.discount?.discountOnProduct || 0
            : product.discount || 0,
        discountType: isDiscountMembership
          ? clientMembership.discount?.productDiscountType || '%'
          : isMembershipProduct
            ? clientMembership.discount?.productDiscountType || '%'
            : product.discountType || '%',
        mrp: product.mrp,
        price: product.sellPrice,
        amount: calculateItemAmount({
          price: product.sellPrice,
          quantity: 1,
          discount: isDiscountMembership
            ? clientMembership.discount?.discountOnProduct || 0
            : isMembershipProduct
              ? clientMembership.discount?.discountOnProduct || 0
              : product.discount || 0,
          discountType: isDiscountMembership
            ? clientMembership.discount?.productDiscountType || '%'
            : isMembershipProduct
              ? clientMembership.discount?.productDiscountType || '%'
              : product.discountType || '%'
        }),
        membershipApplied: isDiscountMembership || isMembershipProduct,
        packageApplied: false,
        discountDisabledByCoupon: false
      };

      updateBillTotals(state);
    },

    addProductsRow: (state) => {
      state.products.push({
        productName: '',
        product: '',
        quantity: 0,
        providerStaff: null,
        discount: 0,
        mrp: 0,
        discountType: '%',
        taxStatus: 'noGst',
        price: 0,
        amount: 0,
        membershipApplied: false,  // Add this
        packageApplied: false,     // Add this
        discountDisabledByCoupon: false // Add this
      });
    },

    deleteProductsRow: (state, action) => {
      const { index } = action.payload;
      state.products.splice(index, 1);

      updateBillTotals(state);
    },

    handleServicesInputChange: (state, action) => {
      const { name, value, index } = action.payload;
      const service = state.services[index];

      if (service) {
        // Don't allow discount changes if coupon is applied or it's a membership/package service
        if ((name === 'discount' || name === 'discountType') &&
          (service.discountDisabledByCoupon || service.membershipApplied || service.packageApplied)) {
          return;
        }

        let updatedValue = value;

        // For inclusive items, discount is applied on taxable value
        const serviceGstRate = Number(service.gst ?? 5);
        const serviceMaxDiscountBase = (service.taxStatus === 'withTax' && serviceGstRate > 0)
          ? (Number(service.price) / (1 + serviceGstRate / 100)) * Number(service.quantity)
          : Number(service.price * service.quantity);

        if (name === 'discount' && service.discountType === '%' && Number(value) > 99) {
          updatedValue = 0;
        }
        // if (name === 'discount' && service.discountType === 'INR' && Number(value) > serviceMaxDiscountBase) {
        //   updatedValue = 0;
        // }
        if (
            name === 'discount' &&
            service.discountType === 'INR' &&
            serviceMaxDiscountBase > 0 &&
            Number(value) > serviceMaxDiscountBase
          ) {
            updatedValue = 0;
          }

        // ✅ If changing discountType to % and previous discount is > 100 → reset discount to 0
        if (name === 'discountType' && value === '%' && Number(service.discount) > 99) {
          service.discount = 0;
        }
        // if (name === 'discountType' && value === 'INR' && Number(service.discount) > serviceMaxDiscountBase) {
        //   service.discount = 0;
        // }
        if (
          name === 'discountType' &&
          value === 'INR' &&
          serviceMaxDiscountBase > 0 &&
          Number(service.discount) > serviceMaxDiscountBase
        ) {
          service.discount = 0;
        }

        service[name] = updatedValue;

        if (name === 'discount' || name === 'discountType' || name === 'quantity' || name === 'price') {
          service.amount = calculateItemAmount(service);
        }
      }

      // Recalculate totals
      updateBillTotals(state);
    },

    handleServicesSuggestedClick: (state, action) => {
      const { service, index } = action.payload;
      if (index === null || index === undefined) return;

      state.services[index] = {
        serviceName: service.serviceName,
        service: service._id,
        category: service.category,
        quantity: 1,
        discount: service.discount || 0,
        discountType: service.discountType || '%',
        serviceProvider: [],
        startDate: '',
        endDate: '',
        price: service.price,
        amount: calculateItemAmount({
          price: service.price,
          quantity: 1,
          discount: service.discount || 0,
          discountType: service.discountType || '%'
        }),
        // gst: service.gst || 5, // Default GST for services
        taxStatus: service.taxStatus || 'noGst', // Default tax status
      };

      // Use updateBillTotals instead of manual calculation to maintain reward discounts
      updateBillTotals(state);

    },

    addServicesRow: (state) => {
      state.services.push({
        serviceName: '',
        service: '',
        category: '',
        quantity: 0,
        discount: 0,
        discountType: '%',
        serviceProvider: [],
        // gst: 5,
        taxStatus: 'noGst',
        serviceProviderName: '',
        startDate: '',
        endDate: '',
        price: 0,
        // gst: 5, // Default GST for services
        taxStatus: 'noGst', // Default tax status
        amount: 0,
      });
    },

    deleteServicesRow: (state, action) => {
      const { index } = action.payload;
      state.services.splice(index, 1);

      // Check if there are any remaining membership-applied services
      const hasRemainingMembershipServices = state.services.some(
        service => service.membershipApplied
      );

      // Check if there are any remaining package-applied services
      const hasRemainingPackageServices = state.services.some(
        service => service.packageApplied
      );

      // Update useMembership flag if needed
      if (!hasRemainingMembershipServices && state.useMembership) {
        state.useMembership = false;
      }

      // Update usePackage flag if needed
      if (!hasRemainingPackageServices && state.usePackage) {
        state.usePackage = false;
        state.packageUniqueId = null;
      }

      updateBillTotals(state);
    },


    // handleTaxeInputChange: (state, action) => {
    //   const { taxType, SGST, PGST } = action.payload;
    //   state.taxes = { taxType, SGST, PGST };

    //   // Instead of manual calculation, use updateBillTotals to ensure consistency
    //   updateBillTotals(state);
    // },
    //     handleTaxeInputChange: (state, action) => {
    //   const { taxType, SGST, PGST } = action.payload;
    //   state.taxes = { taxType, SGST , PGST}; // Default to 5% if not provided
    //   updateBillTotals(state);
    // },

    handleTaxeInputChange: (state, action) => {
      const { taxType, SGST, PGST, productTaxType, productSGST, productPGST, isProductTax = false } = action.payload;

      if (isProductTax) {
        state.productTaxes = {
          productTaxType,
          productSGST: productSGST || 0,
          productPGST: productPGST || 0
        };
      } else {
        state.taxes = {
          taxType,
          SGST: SGST || 0,
          PGST: PGST || 0
        };

        // const newStatus = (taxType === 'Exclusive' ? 'withoutTax' : (taxType === 'Inclusive' ? 'withTax' : 'noGst'));
        // const totalRate = (Number(SGST) || 0) + (Number(PGST) || 0);
        // const newStatus =
        //   totalRate === 0 ? 'noGst'
        //   : taxType === 'Exclusive' ? 'withoutTax'
        //   : 'withTax';
        // state.services.forEach(item => { item.taxStatus = newStatus; });
        // state.memberships.forEach(item => { item.taxStatus = newStatus; });
        // state.packages.forEach(item => { item.taxStatus = newStatus; });
      }

      updateBillTotals(state);
    },
    handleRewardPointChange: (state, action) => {
      const { selectedValue, usedRewardCount = 0, perRewardPointsPrice } = action.payload;
      state.useRewardPoint = selectedValue;
      state.usedRewardCount = usedRewardCount;
      state.perRewardPointValue = perRewardPointsPrice || 0;
      updateBillTotals(state);
    },

    handleRewardsInputChange: (state, action) => {
      const { usedRewardCount, perRewardPointsPrice, availableRewardPoints } = action.payload;

      // Calculate current subtotal to validate points
      const subTotal = calculateSubTotal([...state.services, ...state.products]);
      const maxRewardPointsBySubtotal = Math.floor(subTotal / perRewardPointsPrice);

      // Also consider available points
      const maxValidRewardCount = Math.min(availableRewardPoints, maxRewardPointsBySubtotal);
      const validRewardCount = Math.min(usedRewardCount, maxValidRewardCount);

      state.usedRewardCount = validRewardCount;
      state.perRewardPointValue = perRewardPointsPrice || 0;

      updateBillTotals(state);
    },

    handleAmountsPaidInputChange: (state, action) => {
      const { name, value, index, advanceGiven, setSnackbar } = action.payload;
      const payment = state.amountPaid[index];
      if (!payment) return;

      if (name === 'amount') {
        const numericValue = Number(value) || 0;

        if (payment.paymentType === 'Wallet') {
          const walletBalance = advanceGiven || 0;

          if (numericValue > walletBalance) {
            setSnackbar({
              open: true,
              message: `The entered amount exceeds your wallet balance of ₹${walletBalance}.`,
              severity: 'error'
            });
            return;
          }
        }

        const totalPaid = state.amountPaid.reduce((acc, p, i) =>
          acc + (i === index ? numericValue : Number(p.amount || 0)), 0);

        if (totalPaid > state.amountPayable) {
          setSnackbar({
            open: true,
            message: 'Total paid exceeds bill amount. Please recharge wallet instead.',
            severity: 'error'
          });
          return;
        }

        payment.amount = numericValue;
      }

      if (name === 'paymentType') {
        // Allow editing of payment type first
        payment.paymentType = value;

        // Wallet specific logic
        if (value === 'Wallet') {
          const walletBalance = advanceGiven || 0;

          if (walletBalance <= 0) {
            setSnackbar({
              open: true,
              message: 'Your wallet balance is zero. Please recharge your wallet.',
              severity: 'error'
            });
            // Reset wallet selection (don't override forcibly)
            payment.paymentType = '';
            return;
          }

          const remainingAmount = state.amountPayable -
            state.amountPaid.reduce((acc, p, i) =>
              i !== index ? acc + Number(p.amount || 0) : acc, 0);

          if (walletBalance < remainingAmount) {
            setSnackbar({
              open: true,
              message: `Wallet balance ₹${walletBalance} is less than remaining amount. Setting to ₹${walletBalance}`,
              severity: 'warning'
            });
            payment.amount = walletBalance;
          } else {
            const remainingWalletBalance = walletBalance - remainingAmount;
            setSnackbar({
              open: true,
              message: `₹${remainingAmount} deducted from wallet. Balance left: ₹${remainingWalletBalance}.`,
              severity: 'info'
            });
            payment.amount = remainingAmount;
          }
        }
      }

      if (name !== 'amount' && name !== 'paymentType') {
        payment[name] = value;
      }

      // Final calculation
      const totalPaid = state.amountPaid.reduce((acc, p) => acc + Number(p.amount || 0), 0);
      state.paidDues = Math.max(state.amountPayable - totalPaid, 0);
    },

    deleteAmountsPaidRow: (state, action) => {
      const { index } = action.payload;
      state.amountPaid.splice(index, 1);

      // Recalculate dues
      const totalPaid = state.amountPaid.reduce((acc, p) => acc + Number(p.amount || 0), 0);
      state.paidDues = Math.max(state.amountPayable - totalPaid, 0);
    },
    handleCouponInputChange: (state, action) => {
      const { item } = action.payload;

      if (!item) {
        // Coupon removed: Restore original discounts
        state.coupon = { couponId: null, couponCode: '', discountAmount: 0 };

        // Restore services to their original discounts
        state.services = state.services.map(service => {
          if (service.discountDisabledByCoupon) {
            const originalDiscount = service.originalDiscount || 0;
            const originalDiscountType = service.originalDiscountType || '%';
            return {
              ...service,
              discount: originalDiscount,
              discountType: originalDiscountType,
              amount: calculateItemAmount({
                price: service.price,
                quantity: service.quantity,
                discount: originalDiscount,
                discountType: originalDiscountType
              }),
              discountDisabledByCoupon: false,
              originalDiscount: undefined,
              originalDiscountType: undefined
            };
          }
          return service;
        });

        // Restore products to their original discounts
        state.products = state.products.map(product => {
          if (product.discountDisabledByCoupon) {
            const originalDiscount = product.originalDiscount || 0;
            const originalDiscountType = product.originalDiscountType || '%';
            return {
              ...product,
              discount: originalDiscount,
              discountType: originalDiscountType,
              amount: calculateItemAmount({
                price: product.price,
                quantity: product.quantity,
                discount: originalDiscount,
                discountType: originalDiscountType
              }),
              discountDisabledByCoupon: false,
              originalDiscount: undefined,
              originalDiscountType: undefined
            };
          }
          return product;
        });
      } else {
        // Apply new coupon
        const { discount, discountType, _id, couponCode, maximumDiscountAmount } = item;
        const isPercentage = discountType === '%';

        const priceSubTotal = calculatePriceSubTotal([...state.services, ...state.products]);

        // Calculate total discount amount (for reference)
        let totalDiscountAmount = isPercentage
          ? Math.min(
            (priceSubTotal * discount) / 100,
            maximumDiscountAmount || priceSubTotal
          )
          : Math.min(discount, priceSubTotal);

        // Calculate equal discount percentage for all items
        const equalDiscountPercentage = (totalDiscountAmount / priceSubTotal) * 100;

        // Apply equal discount percentage to all services
        state.services = state.services.map(service => {
          const itemAmount = service.price * service.quantity;
          const itemDiscount = (itemAmount * equalDiscountPercentage) / 100;
          return {
            ...service,
            originalDiscount: service.discount,
            originalDiscountType: service.discountType,
            discount: equalDiscountPercentage,
            discountType: '%',
            amount: (itemAmount - itemDiscount).toFixed(2),
            discountDisabledByCoupon: true
          };
        });

        // Apply equal discount percentage to all products
        state.products = state.products.map(product => {
          const itemAmount = product.price * product.quantity;
          const itemDiscount = (itemAmount * equalDiscountPercentage) / 100;
          return {
            ...product,
            originalDiscount: product.discount,
            originalDiscountType: product.discountType,
            discount: equalDiscountPercentage,
            discountType: '%',
            amount: (itemAmount - itemDiscount).toFixed(2),
            discountDisabledByCoupon: true
          };
        });

        // Store coupon info (but don't apply additional discount at total level)
        state.coupon = {
          couponId: _id,
          couponCode,
          discountAmount: 0 // Set to 0 since discount is already applied at item level
        };

        // Disable other discount types when coupon is applied
        state.useMembership = false;
        state.usePackage = false;
        state.useRewardPoint = false;
        state.usedRewardCount = 0;
      }

      // Update totals (will calculate based on item amounts)
      updateBillTotals(state);
    },
    handleMembershipInputChange: (state, action) => {
      const { useMembership, item, selectedServices, membershipUniqueId } = action.payload;
      state.useMembership = useMembership;
      state.membershipUniqueId = membershipUniqueId || []; // Store in state

      if (useMembership) {
        // Clear existing membership services
        state.services = state.services.filter(service => !service.membershipApplied);
        //bugfix: basically when we are pulling bill from the draft the flag eachservice have
        // should be "membershipApplied: true" but its not there when pulling from draft so thatswhy 
        // unapplying from memberships is not removing the memberships
        // we can either use membershipUniqueId or we can add membershipApplied when we pull

        if (item.membershipType === 'discountType') {
          // For discount memberships, apply discounts to existing items
          state.products.forEach(product => {
            if (!product.membershipApplied) {
              product.originalDiscount = product.discount; // Store original
              product.originalDiscountType = product.discountType;
              product.discount = item.discount?.discountOnProduct || 0;
              product.discountType = item.discount?.productDiscountType || '%';
              product.membershipApplied = true;
              product.amount = calculateItemAmount(product);
            }
          });

          state.services.forEach(service => {
            if (!service.membershipApplied && !service.packageApplied) {
              service.originalDiscount = service.discount; // Store original
              service.originalDiscountType = service.discountType;
              service.discount = item.discount?.discountOnService || 0;
              service.discountType = item.discount?.serviceDiscountType || '%';
              service.membershipApplied = true;
              service.amount = calculateItemAmount(service);
            }
          });
        } else {
          // For service memberships, add new services
          state.services = [...state.services, ...selectedServices];
        }

        // Clear any coupon
        state.coupon = { couponId: null, couponCode: '', discountAmount: 0 };
      // } else {
      //   // COMPLETELY remove membership services (like package does)
      //   state.services = state.services.filter(service => !service.membershipApplied);
      } else {
      // Remove only services that were ADDED by a service-type membership.
      // Services that just got a discount (they have originalDiscount) are kept.
      state.services = state.services.filter(
        service => !service.membershipApplied || service.originalDiscount !== undefined
      );

      // Restore the original discount on the kept services
      state.services.forEach(service => {
        if (service.membershipApplied) {
          service.discount = service.originalDiscount || 0;
          service.discountType = service.originalDiscountType || '%';
          service.membershipApplied = false;
          service.amount = calculateItemAmount(service);
          delete service.originalDiscount;
          delete service.originalDiscountType;
        }
      });

        // Reset discounts for products that had membership discounts
        state.products.forEach(product => {
          if (product.membershipApplied) {
            product.discount = product.originalDiscount || 0;
            product.discountType = product.originalDiscountType || '%';
            product.membershipApplied = false;
            product.amount = calculateItemAmount(product);
            // Clean up temporary fields
            delete product.originalDiscount;
            delete product.originalDiscountType;
          }
        });
      }

      updateBillTotals(state);
    },
    syncMembershipState: (state) => {
      // Check if there are any services with membershipApplied
      const hasMembershipServices = state.services.some(
        service => service.membershipApplied
      );

      // Update the flag if needed
      if (hasMembershipServices && !state.useMembership) {
        state.useMembership = true;
      } else if (!hasMembershipServices && state.useMembership) {
        state.useMembership = false;
      }
    },
    handlePackageInputChange: (state, action) => {
      const { usePackage, item, selectedServices, packageUniqueId } = action.payload;
      state.usePackage = usePackage;

      // ✅ Ensure both are arrays
      const existingIds = Array.isArray(state.packageUniqueId) ? state.packageUniqueId : [];
      const newIds = Array.isArray(packageUniqueId) ? packageUniqueId : [];

      if (usePackage) {
        // Merge and deduplicate package IDs
        state.packageUniqueId = [...new Set([...existingIds, ...newIds])];

        // Update services: remove previously applied package services, add new
        state.services = [
          ...state.services.filter(service => !service.packageApplied),
          ...selectedServices
        ];
      } else {
        // Clear applied package services and IDs
        state.services = state.services.filter(service => !service.packageApplied);
        state.packageUniqueId = [];
      }

      updateBillTotals(state);
    },

    syncPackageState: (state) => {
      // Check if there are any services with packageApplied
      const hasPackageServices = state.services.some(
        service => service.packageApplied
      );

      // Update the flag if needed
      if (hasPackageServices && !state.usePackage) {
        state.usePackage = true;
      } else if (!hasPackageServices && state.usePackage) {
        state.usePackage = false;
        state.packageUniqueId = hasPackageServices ?
          state.packages.map(pkg => pkg.packageId) : [];
      }
    },

    // setAppointmentById: (state, action) => {
    //   console.log(action.payload, 'payloadAppointmnt')
    //   const { appointment } = action.payload;
    //   const { clientName, clientNumber, gender, serviceSelected, _id } = appointment;
    //   console.log(appointment, 'appoinmentttttId')
    //   const services = (serviceSelected || []).map(service => ({
    //     serviceName: service.service?.serviceName || '',
    //     service: service.service?._id || '',
    //     category: service.service?.category || '',
    //     quantity: 1,
    //     discount: 0,
    //     discountType: '%',
    //     serviceProvider: [service.serviceProvider?._id] || [],
    //     serviceProviderName: service.serviceProvider?.name || '',
    //     startDate: '',
    //     endDate: '',
    //     price: service.service?.price || 0,
    //     amount: service.service?.price || 0,
    //   }));

    //   const subTotal = services.reduce((sum, service) => sum + service.amount, 0);
    //   const { SGSTAmount } = calculateTaxes(subTotal, state.taxes.taxType, state.taxes.SGST);
    //   const totalAmount = subTotal + SGSTAmount;

    //   return {
    //     ...state,
    //     appointmentId: _id,
    //     clientName,
    //     clientNumber,
    //     gender,
    //     services,
    //     subTotal: Math.round(subTotal),
    //     totalAmount: Math.round(totalAmount),
    //     amountPayable: Math.round(totalAmount),
    //     paidDues: Math.round(totalAmount),
    //     useMembership: false,
    //     coupon: { couponId: null, couponCode: '', discountAmount: 0 },
    //     amountPaid: [{
    //       paymentType: 'Cash',
    //       amount: '',
    //     }],
    //   };
    // },
    setAppointmentById: (state, action) => {
      const { appointment } = action.payload;
      const { clientName, clientNumber, gender, serviceSelected, _id, amountPaid } = appointment;

      const services = (serviceSelected || []).map(service => ({
        serviceName: service.service?.serviceName || '',
        service: service.service?._id || '',
        category: service.service?.category || '',
        quantity: 1,
        discount: 0,
        discountType: '%',
        serviceProvider: [service.serviceProvider?._id] || [],
        serviceProviderName: service.serviceProvider?.name || '',
        startDate: '',
        endDate: '',
        price: service.service?.price || 0,
        amount: service.service?.price || 0,
      }));

      const subTotal = services.reduce((sum, service) => sum + service.amount, 0);
      const { SGSTAmount, PGSTAmount } = calculateTaxes(subTotal, state.taxes.taxType, state.taxes.SGST, state.taxes.PGST);
      const totalAmount = subTotal + SGSTAmount + PGSTAmount;

      return {
        ...state,
        appointmentId: _id,
        clientName,
        clientNumber,
        gender,
        services,
        subTotal: Math.round(subTotal),
        totalAmount: Math.round(totalAmount),
        amountPayable: Math.round(totalAmount),
        paidDues: Math.round(totalAmount),
        useMembership: false,
        coupon: { couponId: null, couponCode: '', discountAmount: 0 },
        // ✅ agar API se amountPaid aaye to use karo, otherwise default me totalAmount fill karo
        amountPaid: (amountPaid && amountPaid.length > 0)
          ? amountPaid
          : [{
            paymentType: 'Cash',
            amount: Math.round(totalAmount), // 👈 default me autofill
          }],
      };
    },

    // setDraftBill: (state, action) => {
    //   const { draftBill } = action.payload;

    //   if (!draftBill) return state;

    //   const { clientName, clientNumber, gender, _id } = draftBill;

    //   const services = (draftBill.services || []).map(service => ({
    //     serviceName: service?.serviceName || '',
    //     service: service._id || '',
    //     category: service.category || '',
    //     quantity: 1,
    //     discount: 0,
    //     discountType: '%',
    //     serviceProvider: service.serviceProvider || [],
    //     startDate: '',
    //     endDate: '',
    //     price: service?.price || 0,
    //     amount: service?.price || 0,
    //   }));

    //   const products = draftBill.products || [];

    //   const subTotal = [...services, ...products].reduce((sum, item) => sum + (item.amount || 0), 0);
    //   const { SGSTAmount } = calculateTaxes(subTotal, state.taxes.taxType, state.taxes.SGST);
    //   const totalAmount = subTotal + SGSTAmount;

    //   return {
    //     ...state,
    //     // appointmentId: _id,
    //     clientName,
    //     clientNumber,
    //     gender,
    //     services,
    //     products,
    //     subTotal: Math.round(subTotal),
    //     totalAmount: Math.round(totalAmount),
    //     amountPayable: Math.round(totalAmount),
    //     paidDues: Math.round(totalAmount),
    //     draftBillId: _id || '',
    //     amountPaid: draftBill.amountPaid || state.amountPaid,
    //     coupon: draftBill.coupon || { couponId: null, couponCode: '', discountAmount: 0 },
    //     useMembership: draftBill.useMembership || false,
    //     usePackage: draftBill.usePackage || false,
    //     packageUniqueId: draftBill.packageUniqueId || [],
    //   };
    // },
    // In your billSlice.js
    setDraftBill: (state, action) => {
      const { draftBill } = action.payload;

      if (!draftBill) return;

      const {
        clientName,
        clientNumber,
        gender,
        _id,
        services,
        products,
        useMembership,
        usePackage,
        useRewardPoint,
        subTotal,
        totalAmount,
        amountPayable,
        paidDues,
        amountPaid,
        coupon,
        taxes,
        productTaxes,
        discount,
        notes,
        noteForClient,
        dateOfBilling,
        timeOfBilling,
        anniversary,
        birthday,
        clientAddress,
        clientGstNumber,
        sendSms,
        sendWhatsapp,
      } = draftBill;

      // Process services to preserve membership/package info conditionally
      const processedServices = (services || []).map(service => {
        let obj = {
          serviceName: service?.serviceName || '',
          service: service.service || service._id || '',
          category: service.category || '',
          quantity: service.quantity || 1,
          discount: service.discount || 0,
          discountType: service.discountType || '%',
          serviceProvider: service.serviceProvider || [],
          startDate: service.startDate || '',
          endDate: service.endDate || '',
          price: service?.price || 0,
          amount: service?.amount || 0,
          gst: service.gst || 5,
          taxStatus: service.taxStatus || 'noGst'
        };

        // Preserve membership info only if exists
        if ('membershipApplied' in service) obj.membershipApplied = service.membershipApplied;
        if ('membershipUniqueId' in service) obj.membershipUniqueId = service.membershipUniqueId;

        // Preserve package info only if exists
        if ('packageApplied' in service) obj.packageApplied = service.packageApplied;
        if ('packageUniqueId' in service) obj.packageUniqueId = service.packageUniqueId;

        // Preserve original discount values only if exist
        if ('originalDiscount' in service) obj.originalDiscount = service.originalDiscount;
        if ('originalDiscountType' in service) obj.originalDiscountType = service.originalDiscountType;

        // Preserve coupon-related flag only if exists
        if ('discountDisabledByCoupon' in service) obj.discountDisabledByCoupon = service.discountDisabledByCoupon;

        return obj;
      });

      // Collect unique membership and package IDs
      const membershipUniqueIds = [
        ...new Set(
          processedServices
            .map(s => s.membershipUniqueId)
            .filter(id => id) // remove undefined/null/empty
        ),
      ];

      const packageUniqueIds = [
        ...new Set(
          processedServices
            .map(s => s.packageUniqueId)
            .filter(id => id) // remove undefined/null/empty
        ),
      ];

      // Process products to preserve membership/package info conditionally
      const processedProducts = (products || []).map(product => {
        let obj = {
          productName: product?.productName || '',
          product: product.product || product._id || '',
          quantity: product.quantity || 1,
          providerStaff: product.providerStaff || null,
          discount: product.discount || 0,
          discountType: product.discountType || '%',
          // mrp: product.m rp || 0,
          price: product.price || 0,
          amount: product.amount || 0,
          gst: product.gst || 18,
          taxStatus: product.taxStatus || 'noGst'
        };

        // Preserve membership/package info if exists
        if ('membershipApplied' in product) obj.membershipApplied = product.membershipApplied;
        if ('packageApplied' in product) obj.packageApplied = product.packageApplied;

        // Preserve coupon-related flag only if exists
        if ('discountDisabledByCoupon' in product) obj.discountDisabledByCoupon = product.discountDisabledByCoupon;

        // Preserve original discount values if exist
        if ('originalDiscount' in product) obj.originalDiscount = product.originalDiscount;
        if ('originalDiscountType' in product) obj.originalDiscountType = product.originalDiscountType;

        return obj;
      });

      // Mutate state directly to populate data
      state.clientName = clientName;
      state.clientNumber = clientNumber;
      state.gender = gender;
      state.anniversary = anniversary || '';
      state.birthday = birthday || '';
      state.clientAddress = clientAddress || '';
      state.clientGstNumber = clientGstNumber || '';
      state.dateOfBilling = dateOfBilling || getCurrentDateInFormat();
      state.timeOfBilling = timeOfBilling || getCurrentTime();
      state.services = processedServices;
      state.products = processedProducts;
      state.amountPaid = amountPaid || [{ paymentType: 'Cash', amount: '' }];
      state.coupon = coupon || { couponId: null, couponCode: '', discountAmount: 0 };
      state.discount = discount || { discountType: '%', discount: '' };
      state.taxes = taxes || { taxType: 'Inclusive', SGST: 0, PGST: 0 };
      state.productTaxes = productTaxes || { productTaxType: 'Inclusive', productSGST: 0, productPGST: 0 };
      state.useMembership = useMembership || false;
      state.usePackage = usePackage || false;
      state.useRewardPoint = draftBill.useRewardPoint || false;
      state.usedRewardCount = draftBill.usedRewardCount || 0;
      state.membershipUniqueId = membershipUniqueIds;
      state.packageUniqueId = packageUniqueIds;
      state.draftBillId = _id || '';
      state.notes = notes || '';
      state.noteForClient = noteForClient || '';
      state.sendSms = sendSms !== undefined ? sendSms : true;
      state.sendWhatsapp = sendWhatsapp !== undefined ? sendWhatsapp : true;
      state.amountAdded = draftBill.amountAdded || 0;
      state.walletAmount = draftBill.walletAmount || 0;
      state.wallets = draftBill.wallets || [];

      //   return {
      //   ...state,
      //   // Client information
      //   clientName,
      //   clientNumber,
      //   gender,
      //   anniversary: anniversary || '',
      //   birthday: birthday || '',
      //   clientAddress: clientAddress || '',
      //   clientGstNumber: clientGstNumber || '',

      //   // Date and time
      //   dateOfBilling: dateOfBilling || getCurrentDateInFormat(),
      //   timeOfBilling: timeOfBilling || getCurrentTime(),

      //   // Items
      //   services: processedServices,
      //   products: processedProducts,

      //   // Financials
      //   subTotal: subTotal || 0,
      //   totalAmount: totalAmount || 0,
      //   amountPayable: totalAmount || 0,
      //   paidDues: paidDues || 0,

      //   // Payment
      //   amountPaid: amountPaid || state.amountPaid,

      //   // Discounts and taxes
      //   coupon: coupon || { couponId: null, couponCode: '', discountAmount: 0 },
      //   discount: discount || { discountType: '%', discount: '' },
      //   taxes: taxes || { taxType: 'Inclusive', SGST: 0, PGST: 0 },
      //   productTaxes: productTaxes || { productTaxType: 'Inclusive', productSGST: 0, productPGST: 0 },

      //   // Membership and package info
      //   useMembership: useMembership || false,
      //   useRewardPoint: false,
      //   usePackage: usePackage || false,
      //   membershipUniqueId: membershipUniqueIds || [],
      //   packageUniqueId: packageUniqueIds || [],

      //   // Other
      //   draftBillId: _id || '',
      //   notes: notes || '',
      //   sendSms: sendSms !== undefined ? sendSms : true, // gadbad code
      //   sendWhatsapp: sendWhatsapp !== undefined ? sendWhatsapp : true  // gadbad code
      // };

      // console.log(">>>>>>>> This is draftBill State that is not", draftBillState)
      // state.bill = draftBillState
      // return state.bill
      // Recalculate all totals including GST to ensure accuracy and prevent calculation mistakes
      updateBillTotals(state);
    },

    updateAmountPaid: (state, action) => {
      const { index, amount } = action.payload;
      if (state.amountPaid[index]) {
        state.amountPaid[index].amount = amount;

        // Recalculate dues
        const totalPaid = state.amountPaid.reduce((acc, p) => acc + Number(p.amount || 0), 0);
        state.paidDues = Math.max(state.amountPayable - totalPaid, 0);
      }
    },
    addAmountsPaidRow: (state, action) => {
      const { paymentType = 'Cash', amount = '' } = action.payload || {};

      // Calculate remaining amount
      const totalPaid = state.amountPaid.reduce(
        (acc, p) => acc + Number(p.amount || 0), 0
      );
      const remainingAmount = state.amountPayable - totalPaid;

      // Add new row
      state.amountPaid.push({
        paymentType,
        amount: remainingAmount > 0 ? remainingAmount : amount
      });

      // Recalculate dues
      state.paidDues = Math.max(
        state.amountPayable -
        state.amountPaid.reduce((acc, p) => acc + Number(p.amount || 0), 0
        ));
    },
    // Add this to your reducers object in billSlice
    setBillState: (state, action) => {
      return {
        ...state,
        ...action.payload
      };
    },
    // Add specific reducers for membership and package modes
    addMembershipRow: (state) => {
    // state.memberships.push({
    //       membershipName: '',
    //     membershipId: '',
    //     membershipType: '',
    //     quantity: 1,
    //     discount: 0,
    //     discountType: '%',
    //     serviceProvider: [],
    //     startDate: '',
    //     endDate: '',
    //     price: 0,
    //     amount: 0,
    //   });
    state.memberships.push({
  membershipName: '',
  membershipId: '',
  membershipType: '',
  quantity: 1,
  discount: 0,
  discountType: '%',
  serviceProvider: [],
  startDate: '',
  endDate: '',
  price: 0,
  gst: 5,
  taxStatus: 'noGst',
  amount: 0,
});
    },
    deleteMembershipRow: (state, action) => {
      const { index } = action.payload;
      state.memberships.splice(index, 1);
      updateBillTotals(state);
    },
    handleMembershipBillChange: (state, action) => {
      const { name, value, index } = action.payload;
      const membership = state.memberships[index];

      if (membership) {
        // membership[name] = value;

        let updatedValue = value;

        // For inclusive items, discount is applied on taxable value
        const membershipGstRate = Number(membership.gst ?? 5);
        const membershipMaxDiscountBase = (membership.taxStatus === 'withTax' && membershipGstRate > 0)
          ? (Number(membership.price) / (1 + membershipGstRate / 100)) * Number(membership.quantity)
          : Number(membership.price * membership.quantity);

        if (name === 'discount' && membership.discountType === '%' && Number(value) > 99) {
          updatedValue = 0;
        }
        // if (name === 'discount' && membership.discountType === 'INR' && Number(value) > membershipMaxDiscountBase - 1) {
        //   updatedValue = 0;
        // }
        if (
          name === 'discount' &&
          membership.discountType === 'INR' &&
          membershipMaxDiscountBase > 0 &&
          Number(value) > membershipMaxDiscountBase - 1
        ) {
          updatedValue = 0;
        }

        // ✅ If changing discountType to % and previous discount is > 100 → reset discount to 0
        if (name === 'discountType' && value === '%' && Number(membership.discount) > 99) {
          membership.discount = 0;
        }
        // if (name === 'discountType' && value === 'INR' && Number(membership.discount) > membershipMaxDiscountBase - 1) {
        //   membership.discount = 0;
        // }
        if (
            name === 'discountType' &&
            value === 'INR' &&
            membershipMaxDiscountBase > 0 &&
            Number(membership.discount) > membershipMaxDiscountBase - 1
          ) {
            membership.discount = 0;
          }


        membership[name] = updatedValue;

        if (name === 'discount' || name === 'discountType' || name === 'price' || name === 'quantity') {
          membership.amount = calculateItemAmount(membership);
        }
      }

      updateBillTotals(state);
    },

    handleMembershipBillSuggestedClick: (state, action) => {
      const { membership, index, taxType, SGST, PGST, taxStatus: payloadTaxStatus } = action.payload;
      const membershipDetails = state.memberships[index];

      if (membershipDetails) {
        const basePrice = membership.membershipPrice || membership.price || 0;
        membershipDetails.membershipName = membership.membershipName;
        membershipDetails.membershipId = membership._id;
        membershipDetails.membershipType = membership.membershipType;
        membershipDetails.price = basePrice;
        // membershipDetails.gst = membership.gst || 5;
        membershipDetails.taxStatus = membership.taxStatus || payloadTaxStatus || 
            (taxType === 'Exclusive' ? 'withoutTax' : (taxType === 'Inclusive' ? 'withTax' : 'noGst'));
        membershipDetails.quantity = 1;
        membershipDetails.discount = 0;
        membershipDetails.discountType = '%';
//         membershipDetails.gst = membership.gst || 5;  // add this line before calculateItemAmount
// membershipDetails.taxStatus = membership.taxStatus || 'noGst';
//         membershipDetails.amount = calculateItemAmount(membershipDetails);
membershipDetails.gst = membership.gst ?? 5;
membershipDetails.taxStatus = membership.taxStatus || 'noGst';
membershipDetails.amount = calculateItemAmount({
  price: membershipDetails.price,
  quantity: membershipDetails.quantity,
  discount: membershipDetails.discount,
  discountType: membershipDetails.discountType,
  gst: membershipDetails.gst,
  taxStatus: membershipDetails.taxStatus,
});

        // Update taxes if provided
        // if (taxType && SGST && PGST) {
        //   state.taxes = { taxType, SGST, PGST };
        // }
        if (taxType && SGST && PGST) {
          state.taxes = { taxType, SGST, PGST }; // Default to 5%
        }
      }

      updateBillTotals(state);
    },
    addPackageRow: (state) => {
      state.packages.push({
        packageName: '',
        packageId: '',
        quantity: 1,
        discount: 0,
        discountType: '%',
        serviceProvider: [],
        // gst: 5,
        taxStatus: 'noGst',
        startDate: '',
        endDate: '',
        price: 0,
        amount: 0,
      });
    },

    deletePackageRow: (state, action) => {
      const { index } = action.payload;
      state.packages.splice(index, 1);
      updateBillTotals(state);
    },
    handlePackageBillChange: (state, action) => {
      const { name, value, index } = action.payload;
      const packageItem = state.packages[index];

      if (packageItem) {

        let updatedValue = value;

        // For inclusive items, discount is applied on taxable value
        const packageGstRate = Number(packageItem.gst ?? 5);
        const packageMaxDiscountBase = (packageItem.taxStatus === 'withTax' && packageGstRate > 0)
          ? (Number(packageItem.price) / (1 + packageGstRate / 100)) * Number(packageItem.quantity)
          : Number(packageItem.price * packageItem.quantity);

        if (name === 'discount' && packageItem.discountType === '%' && Number(value) > 99) {
          updatedValue = 0;
        }
        // if (name === 'discount' && packageItem.discountType === 'INR' && Number(value) > packageMaxDiscountBase - 1) {
        //   updatedValue = 0;
        // }
        if (
          name === 'discount' &&
          packageItem.discountType === 'INR' &&
          packageMaxDiscountBase > 0 &&
          Number(value) > packageMaxDiscountBase - 1
        ) {
          updatedValue = 0;
        }

        // ✅ If changing discountType to % and previous discount is > 100 → reset discount to 0
        if (name === 'discountType' && value === '%' && Number(packageItem.discount) > 99) {
          packageItem.discount = 0;
        }
        // if (name === 'discountType' && value === 'INR' && Number(packageItem.discount) > packageMaxDiscountBase - 1) {
        //   packageItem.discount = 0;
        // }
        if (
            name === 'discountType' &&
            value === 'INR' &&
            packageMaxDiscountBase > 0 &&
            Number(packageItem.discount) > packageMaxDiscountBase - 1
          ) {
            packageItem.discount = 0;
          }


        packageItem[name] = updatedValue;

        if (name === 'discount' || name === 'discountType' || name === 'price' || name === 'quantity') {
          packageItem.amount = calculateItemAmount(packageItem);
        }
      }

      updateBillTotals(state);
    },

    handlePackageBillSuggestedClick: (state, action) => {
      const { package: packageItem, index, taxType, SGST, PGST, taxStatus: payloadTaxStatus } = action.payload;
      const servicesPackage = state.packages[index];
      console.log(packageItem, 'packageItem')

      if (servicesPackage) {
        servicesPackage.packageName = packageItem.packageName;
        servicesPackage.packageId = packageItem._id;
        servicesPackage.price = packageItem.price || 0;
        // servicesPackage.gst = packageItem.gst || 5;
        servicesPackage.taxStatus = packageItem.taxStatus || payloadTaxStatus || 
            (taxType === 'Exclusive' ? 'withoutTax' : (taxType === 'Inclusive' ? 'withTax' : 'noGst'));
        servicesPackage.quantity = 1;
        servicesPackage.discount = 0;
        servicesPackage.discountType = '%';
        servicesPackage.gst = packageItem.gst || 5;  // add before calculateItemAmount
servicesPackage.taxStatus = packageItem.taxStatus || 'noGst';
        // servicesPackage.amount = calculateItemAmount({
        //   price: packageItem.price,
        //   quantity: 1,
        //   discount: 0,
        //   discountType: '%'
        // });
        servicesPackage.amount = calculateItemAmount({
  price: packageItem.price,
  quantity: 1,
  discount: 0,
  discountType: '%',
  gst: servicesPackage.gst,
  taxStatus: servicesPackage.taxStatus,
});
        servicesPackage.services = packageItem.services || [];

        // Update taxes if provided
        // if (taxType && SGST && PGST) {
        //   state.taxes = { taxType, SGST, PGST };
        // }
        if (taxType && SGST && PGST) {
          state.taxes = { taxType, SGST, PGST }; // Default to 5%
        }
      }

      updateBillTotals(state);
    },
    addWalletRow: (state) => {
      if (!state.wallets) {
        state.wallets = []; // Ensure wallets array exists
      }
      state.wallets.push([]); // Empty array for service providers
    },

    // Delete wallet row
    // deleteWalletRow: (state, action) => {
    //   const { index } = action.payload;
    //   state.wallets.splice(index, 1);
    //   updateBillTotals(state);
    // },
    // Delete wallet row
    deleteWalletRow: (state, action) => {
      const { index } = action.payload;
      if (state.wallets && state.wallets.length > index) {
        state.wallets.splice(index, 1);
      }
      updateBillTotals(state);
    },

    // Handle wallet input changes
    // handleWalletInputChange: (state, action) => {
    //   const { name, value, index } = action.payload;

    //   if (name === 'walletServiceProvider') {
    //     // For service provider, update the specific wallet array item
    //     if (state.wallets[index]) {
    //       state.wallets[index] = Array.isArray(value) ? value : [value];
    //     }
    //   } else if (name === 'amountAdded') {
    //     // For credit amount, update the single value
    //     state.amountAdded = Number(value) || 0;
    //   } else if (name === 'walletAmount') {
    //     // For amount, update the single value
    //     state.walletAmount = Number(value) || 0;

    //     // Recalculate subtotal for wallet mode
    //     state.subTotal = Number(value) || 0;

    //     // Update other totals
    //     updateBillTotals(state);
    //   }
    // },
    handleWalletInputChange: (state, action) => {
      const { name, value, index } = action.payload;
      console.log(name, 'nameeee', value, 'valueeeee', index, 'indexx')

      if (name === 'walletServiceProvider') {
        if (state.wallets[index]) {
          state.wallets[index] = Array.isArray(value) ? value : [value];
        }
      } else if (name === 'amountAdded') {
        state.amountAdded = Number(value) || 0;
      } else if (name === 'walletAmount') {
        state.walletAmount = Number(value) || 0;
      }

      // Always update totals after any wallet input change
      updateBillTotals(state);
    },


    // Add this new reducer to reset payments when needed
    resetPaymentsIfItemsChanged: (state, action) => {
      const { servicesChanged, productsChanged, amountChanged } = action.payload;

      if (servicesChanged || productsChanged || amountChanged) {
        state.amountPaid = [{
          paymentType: 'Cash',
          amount: state.amountPayable
        }];
        state.paidDues = 0;
      }
    },
  }
});

export const {
  resetBillState,
  handleClientsInputChange,
  handleClientSuggestedClick,
  handleProductsInputChange,
  handleProductsSuggestedClick,
  addProductsRow,
  deleteProductsRow,
  handleServicesInputChange,
  handleServicesSuggestedClick,
  addServicesRow,
  deleteServicesRow,
  handleTaxeInputChange,
  handleRewardPointChange,
  handleRewardsInputChange,
  handleAmountsPaidInputChange,
  addAmountsPaidRow,
  deleteAmountsPaidRow,
  handleCouponInputChange,
  handleMembershipInputChange,
  handlePackageInputChange,
  setAppointmentById,
  updateAmountPaid,
  setDraftBill,
  setBillState,
  syncMembershipState,
  syncPackageState,
  addMembershipRow,
  deleteMembershipRow,
  handleMembershipBillChange,
  handleMembershipBillSuggestedClick,
  handlePackageBillChange,
  addPackageRow,
  deletePackageRow,
  handlePackageBillSuggestedClick,
  addWalletRow,
  deleteWalletRow,
  handleWalletInputChange,
  resetPaymentsIfItemsChanged,
} = billSlice.actions;

export default billSlice.reducer;